import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, ArrowRight } from '@phosphor-icons/react';
import { useSEO } from '@/hooks/useSEO';
import PublicNav from '@/components/layout/PublicNav';
import PublicFooter from '@/components/layout/PublicFooter';
import { DOCS_CATEGORIES, searchArticles } from './docsData';

export default function DocsIndex() {
  const [query, setQuery] = useState('');
  const results = searchArticles(query);

  useSEO({
    title: 'Documentation — YuHasPro',
    description: 'Guides, tutorials, and documentation for the YuHasPro admissions CRM. Learn how to manage leads, pipeline, fees, communication, and automation.',
  });

  return (
    <div className="bg-background text-foreground min-h-screen">
      <PublicNav />

      <main className="pt-32 pb-24 px-6 lg:px-12 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-4" style={{ color: '#E04B2A' }}>Documentation</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-foreground tracking-[-0.02em] leading-[1.08]" data-testid="docs-title">Learn YuHasPro</h1>
          <p className="text-base text-muted-foreground mt-4 max-w-lg mx-auto leading-relaxed">Guides, tutorials, and references for every feature. From first login to advanced automation.</p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-16 relative">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search docs..."
            className="w-full h-11 pl-11 pr-4 rounded-xl border border-border bg-card text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 transition-shadow"
            data-testid="docs-search"
          />
          {query.length >= 2 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto z-10" data-testid="docs-search-results">
              {results.length === 0 ? (
                <p className="px-4 py-4 text-[12px] text-muted-foreground text-center">No results found</p>
              ) : (
                results.map(r => (
                  <Link
                    key={`${r.categoryId}-${r.slug}`}
                    to={`/docs/${r.categoryId}/${r.slug}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/60 transition-colors first:rounded-t-xl last:rounded-b-xl"
                    onClick={() => setQuery('')}
                  >
                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold flex-shrink-0" style={{ color: r.categoryColor, backgroundColor: `${r.categoryColor}12` }}>
                      {r.categoryTitle.split(' ')[0]}
                    </span>
                    <span className="text-[12px] text-foreground truncate">{r.title}</span>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="docs-categories">
          {DOCS_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const firstSlug = cat.articles[0]?.slug || 'overview';
            return (
              <Link
                key={cat.id}
                to={`/docs/${cat.id}/${firstSlug}`}
                className="group p-6 rounded-xl border border-border bg-card hover:border-foreground/15 hover:shadow-sm transition-all duration-300"
                data-testid={`docs-cat-card-${cat.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: `${cat.color}12`, color: cat.color }}>
                    <Icon size={19} weight="bold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-[15px] font-bold text-foreground mb-1.5 tracking-tight">{cat.title}</h2>
                    <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-3.5">{cat.description}</p>
                    <p className="text-[10.5px] text-muted-foreground font-mono uppercase tracking-wider">
                      {cat.articles.length} {cat.articles.length === 1 ? 'article' : 'articles'}
                    </p>
                  </div>
                  <ArrowRight size={14} weight="bold" className="text-muted-foreground/50 group-hover:text-foreground group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
